import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {FieldName, IFrame, IFrameRecord, RowIndex} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameEvalOperation} from '@mintdata/mint-kit/impl/frames/FrameEvalOperation';
import {frameSelectionGrammar} from '@mintdata/mint-kit/impl/frames/FrameGrammar';
import {FrameQueryResult} from '@mintdata/mint-kit/impl/frames/FrameQueryResult';
import {
    FrameSelectExpr,
    FrameSelectionError,
    FrameSelectionParseState,
    RowFilterResult
} from '@mintdata/mint-kit/impl/frames/FrameSelectorTypes';
import {RowFilterKind} from '@mintdata/mint-kit/impl/frames/RowFilterKind';
import * as OhmJs from 'ohm-js';
import {MatchResult} from 'ohm-js';

export class FrameSelectionEngine
{
    private readonly _ohm:OhmJs.Grammar;
    private readonly _frameEval:FrameEvalOperation;
    private readonly _astTraverser:OhmJs.Semantics;

    public constructor()
    {
        this._ohm = OhmJs.grammar(frameSelectionGrammar);
        this._frameEval = new FrameEvalOperation();
        this._astTraverser = this._ohm.createSemantics().addOperation('eval', this._frameEval as any);
    }

    public expressionFromQueryResult(fqr:FrameQueryResult):FrameSelectExpr
    {
        const fieldSelectExpr:string = fqr.selectedFieldRefs.join(',');
        const frameSelectExpr:FrameSelectExpr = `@${fieldSelectExpr}`;

        if( !fqr.hasRowFilter )
            return frameSelectExpr;

        let rowFilterKind = fqr.rowFilterKind;

        if( rowFilterKind === RowFilterKind.All )
            return frameSelectExpr;
        else if( rowFilterKind === RowFilterKind.First )
            return `${frameSelectExpr}[first]`;
        else if( rowFilterKind === RowFilterKind.Last )
            return `${frameSelectExpr}[last]`;
        else if( rowFilterKind === RowFilterKind.ByIndex )
            return `${frameSelectExpr}[${fqr.firstRowIndex}]`;
        else if( rowFilterKind === RowFilterKind.ByRange )
            return `${frameSelectExpr}[${fqr.firstRowIndex}..${fqr.lastRowIndex}]`;
        else if( rowFilterKind === RowFilterKind.ByFilter )
            return `${frameSelectExpr}[${fqr.rowFilterExpr}]`;
        else
            throw new FrameSelectionError(`Unknown rowFilterKind: ${rowFilterKind}`);
    }

    public query(frame:IFrame, frameSelectExpr:FrameSelectExpr):FrameQueryResult
    {
        if( !frame || frame.isEmpty )
            return FrameQueryResult.Empty;

        const match:MatchResult = this._ohm.match(frameSelectExpr);

        if( !match.succeeded() )
            throw new FrameSelectionError(`FrameSelector.query(): Invalid frame selection expression: ${frameSelectExpr}`);

        this._frameEval.resetState(frame.getFieldNames());
        this._astTraverser(match).eval();
        const state:FrameSelectionParseState = this._frameEval.getState();

        // TODO: return set logic for error handling
        // // -> availableFieldNames = [x ,y]
        // // -> fieldNames          = [x ,z]
        // // <- diffColumns         = [z]
        // const diffColumns:Array<FieldName> = _differenceBy(
        // 	state.fieldNames,
        // 	state.availableFieldNames,
        // 	(fieldName:FieldName) => fieldName.toLowerCase()
        // ).map((fieldName:FieldName) => fieldName.toLowerCase());
        //
        // // missing all the requested columns in the source frame
        // if( diffColumns.length === state.fieldNames.length )
        // 	return FrameQueryResult.Empty;
        //
        // // -> fieldNames          = [x ,z]
        // // -> diffColumns         = [z]
        // // <- fieldNames          = [x]
        // if( diffColumns.length )
        // 	state.fieldNames = state.fieldNames.filter(
        // 		(fieldName:FieldName) => diffColumns.indexOf(fieldName.toLowerCase()) === -1
        // 	);

        if( !state.hasRowFilter )
        {
            const projectedFrame:IFrame = frame.withColumnSubset(state.fieldRefs, true);
            const lastIndex:number = projectedFrame.recordCount - 1;
            const rowFilterExpr:string = state.rowFilterExpr;

            return new FrameQueryResult(
                true,
                false,
                projectedFrame,
                state.fieldRefs,
                RowFilterKind.All,
                0,
                lastIndex,
                null,
                rowFilterExpr
            );
        }
        else
        {
            const filterRowsResult:RowFilterResult = this.filterRows(frame, state);
            const filteredFrame:IFrame = filterRowsResult.frame;

            if( filteredFrame.isEmpty )
                return FrameQueryResult.Empty;

            const projectedFrame:IFrame = filteredFrame.withColumnSubset(state.fieldRefs, true);
            const rowFilterExpr:string = filterRowsResult.rowFilterKind === RowFilterKind.ByFilter ? state.rowFilterExpr : null;

            return new FrameQueryResult(
                true,
                true,
                projectedFrame,
                state.fieldRefs,
                filterRowsResult.rowFilterKind,
                filterRowsResult.firstRowIndex,
                filterRowsResult.lastRowIndex,
                filterRowsResult.rowIndices,
                rowFilterExpr
            );
        }
    }

    public select(frame:IFrame, frameSelectExpr:FrameSelectExpr):IFrame
    {
        const frameQueryResult:FrameQueryResult = this.query(frame, frameSelectExpr);
        return frameQueryResult.frame;
    }

    private filterRows(frame:IFrame, state:FrameSelectionParseState):RowFilterResult
    {
        if( state.rowIndexExpr !== null )
            return this._filterRowsViaExactRowIndex(frame, state);
        else if( state.rowRangeFromExpr != null && state.rowRangeToExpr != null )
            return this._filterRowsViaRowIndexRange(frame, state);
        else if( state.rowFilterExpr != null )
            return this._filterRowsViaFilterExpr(frame, state.rowFilterExpr);
        else
            throw new Error(`FrameSelector: unknown FrameSelectionState: ${state}`);
    }

    private _parseRowIndex(frame:IFrame, rowIndexExpr:string, allowRandom:boolean):number
    {
        let rowIndex:number;

        if( rowIndexExpr === FrameSelectionParseState.First )
            rowIndex = 0;
        else if( rowIndexExpr === FrameSelectionParseState.Last ||
            rowIndexExpr === FrameSelectionParseState.N ||
            rowIndexExpr === FrameSelectionParseState.Lowercase_N )
            rowIndex = frame.recordCount - 1;
        else if( allowRandom && rowIndexExpr === FrameSelectionParseState.Random )
            rowIndex = Math.floor(Math.random() * (frame.recordCount - 1));
        else
            rowIndex = parseInt(rowIndexExpr);

        return rowIndex;
    }

    private _filterRowsViaExactRowIndex(frame:IFrame, state:FrameSelectionParseState):RowFilterResult
    {
        const rowIndexExpr:string = state.rowIndexExpr;

        let rowIndex:number;

        try
        {
            rowIndex = this._parseRowIndex(frame, rowIndexExpr, true);
        }
        catch( e )
        {
            throw new FrameSelectionError(`Cannot filter frame rows; invalid rowIndex: ${rowIndexExpr}`);
        }

        const newFrame:IFrame = frame.withRecordsSubset([rowIndex]);

        let rowFilterKind:RowFilterKind;

        if( rowIndexExpr === FrameSelectionParseState.First )
            rowFilterKind = RowFilterKind.First;
        else if( rowIndexExpr === FrameSelectionParseState.Last )
            rowFilterKind = RowFilterKind.Last;
        else if( rowIndexExpr === FrameSelectionParseState.N )
            rowFilterKind = RowFilterKind.Last;
        else if( rowIndexExpr === FrameSelectionParseState.Lowercase_N )
            rowFilterKind = RowFilterKind.Last;
        else
            rowFilterKind = RowFilterKind.ByIndex;

        return new RowFilterResult(newFrame, rowFilterKind, rowIndex, rowIndex);
    }

    private _filterRowsViaRowIndexRange(frame:IFrame, state:FrameSelectionParseState):RowFilterResult
    {
        let fromRowIndex:RowIndex;
        let toRowIndex:RowIndex;

        try
        {
            fromRowIndex = this._parseRowIndex(frame, state.rowRangeFromExpr, false);
        }
        catch( e )
        {
            throw new FrameSelectionError(`Cannot filter frame rows; invalid starting rowIndex: ${state.rowRangeFromExpr}`);
        }

        try
        {
            toRowIndex = this._parseRowIndex(frame, state.rowRangeToExpr, false);
        }
        catch( e )
        {
            throw new FrameSelectionError(`Cannot filter frame rows; invalid ending rowIndex: ${state.rowRangeToExpr}`);
        }

        const recordCount:number = frame.recordCount;

        if( fromRowIndex < 0 || fromRowIndex > recordCount - 1 )
            throw new FrameSelectionError(`Cannot filter frame rows; invalid start rowIndex: ${fromRowIndex} (recordCount: ${recordCount})`);

        if( toRowIndex < 0 || toRowIndex > recordCount - 1 )
            throw new FrameSelectionError(`Cannot filter frame rows; invalid ending rowIndex: ${toRowIndex} (recordCount: ${recordCount})`);

        if( fromRowIndex >= toRowIndex )
            throw new FrameSelectionError(`Cannot filter frame rows; starting rowIndex cannot be larger than (or equal to) ending rowIndex` +
                `(start rowIndex: ${fromRowIndex} ending rowIndex: ${toRowIndex})`);

        const rowIndiciesToInclude:Array<number> = [];

        for( let i = fromRowIndex; i <= toRowIndex; i++ )
            rowIndiciesToInclude.push(i);

        const newFrame:IFrame = frame.withRecordsSubset(rowIndiciesToInclude);

        return new RowFilterResult(
            newFrame,
            RowFilterKind.ByRange,
            fromRowIndex,
            toRowIndex
        );
    }

    private _filterRowsViaFilterExpr(frame:IFrame, filterRowsExpr:string):RowFilterResult
    {
        const records:Array<IFrameRecord> = frame.getRecords();
        const fieldNames:Array<FieldName> = frame.getFieldNames();

        const includedRecordIndicies:Array<number> = [];
        let rowIndex:RowIndex = 0;

        for( let record of records )
        {
            let sb:string = '';

            for( let fieldName of fieldNames )
            {
                const cellValue:IValue = record.getCell(fieldName, true);
                const value:string = cellValue.toString();

                sb += `var ${fieldName} = ${JSON.stringify(value)};\n`;
                sb += `var ${fieldName.toLowerCase()} = ${fieldName};\n`;
            }

            sb += `var __filterExprResult = ${filterRowsExpr};\n`;
            sb += `__filterExprResult;`;

            let isRecordIncluded:boolean = false;

            try
            {
                isRecordIncluded = eval(sb);
            }
            catch( e )
            {
                // ignore; consider as isRecordIncluded == false
            }

            if( isRecordIncluded )
                includedRecordIndicies.push(rowIndex);

            rowIndex++;
        }

        const newFrame:IFrame = frame.withRecordsSubset(includedRecordIndicies);

        return new RowFilterResult(
            newFrame,
            RowFilterKind.ByFilter,
            -1,
            -1,
            includedRecordIndicies
        );
    }
}
